import { useCallback, useEffect, useState } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Send, Loader2, AlertTriangle } from 'lucide-react';

const EMPTY = { name: '', email: '', phone: '', message: '' };
const STATUS = {
  registered: ['Registered', 'bg-emerald-100 text-emerald-700'], verifying: ['Verifying email', 'bg-blue-100 text-blue-700'],
  not_registered: ['Not yet registered', 'bg-amber-100 text-amber-700'], registered_elsewhere: ['Joined via another code', 'bg-slate-200 text-slate-600'],
};
const TH = 'text-left py-3 px-4 text-xs uppercase tracking-wider font-semibold text-brand-text-secondary';

function InviteForm({ code, onSent }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post('/sales/me/invites', form); toast.success(data.message); setForm(EMPTY); onSent(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card className="border border-brand-border bg-white">
      <CardContent className="p-5">
        <form onSubmit={send} className="space-y-3" data-testid="invite-form">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1"><Label className="text-xs">Client Name *</Label><Input value={form.name} required onChange={e => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Client Email *</Label><Input type="email" value={form.email} required onChange={e => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div>
            <div className="space-y-1"><Label className="text-xs">Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="invite-phone" /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Personal Message (optional)</Label>
            <Textarea rows={3} maxLength={1000} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder="e.g. Great speaking with you today. Here is the link to open your account." data-testid="invite-message" /></div>
          <div className="flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            The email includes a welcome note, your code <b className="font-mono">{code}</b> (locked in the link) and a risk disclaimer: profits are not guaranteed.
          </div>
          <Button type="submit" disabled={busy} className="bg-brand-primary text-white" data-testid="invite-send-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <><Send size={14} className="mr-1.5" /> Send Invite Email</>}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

export function InviteClientsTab({ code }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get('/sales/me/invites').then(r => setRows(r.data)).catch(err => toast.error(formatError(err.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="invite-clients-tab">
      <InviteForm code={code} onSent={load} />
      <Card className="border border-brand-border bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="invite-history-table">
          <thead><tr className="border-b border-brand-border bg-brand-bg/50">{['Sent', 'Name', 'Email', 'Email Status', 'Sign-up'].map(h => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>{rows.length === 0 ? <tr><td colSpan={5} className="py-10 text-center text-brand-text-secondary">No invites sent yet</td></tr> : rows.map(r => {
            const [l, c] = STATUS[r.signup_status] || STATUS.not_registered;
            return (
              <tr key={r.id} className="border-b border-brand-border/50">
                <td className="py-3 px-4 text-xs text-brand-text-secondary">{new Date(r.created_at).toLocaleString()}</td>
                <td className="py-3 px-4 font-medium">{r.name}</td>
                <td className="py-3 px-4 text-xs">{r.email}</td>
                <td className="py-3 px-4"><Badge className={`border-0 text-[11px] ${r.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{r.status}</Badge></td>
                <td className="py-3 px-4"><Badge className={`border-0 text-[11px] ${c}`} data-testid={`invite-signup-${r.id}`}>{l}</Badge></td>
              </tr>
            );
          })}</tbody>
        </table>
      </Card>
    </div>
  );
}
